/**
 * @file services.js
 * @description Frontend API Service Layer connecting React components to backend REST endpoints.
 */

import api from "./axios";
import axios from "axios";
import imageCompression from 'browser-image-compression';

/**
 * Compress image file if size exceeds 1MB threshold before uploading.
 *
 * @async
 * @param {File} file - Original image file.
 * @returns {Promise<File>} Compressed File object or original file if compression skipped.
 */
export const compressImageIfNeeded = async (file) => {
  // If not a file, or size <= 1MB (1048576 bytes), no compression needed
  if (!file || !file.size || file.size <= 1048576) {
    return file;
  }
  try {
    const options = {
      maxSizeMB: 0.8,          // Target size 800KB to safely pass Nginx limits
      maxWidthOrHeight: 2000,
      useWebWorker: true,
    };
    const compressedBlob = await imageCompression(file, options);
    // Wrap in File to preserve the original filename for backend extension validation
    return new File([compressedBlob], file.name || "image.jpg", {
      type: compressedBlob.type || file.type || "image/jpeg",
    });
  } catch (error) {
    console.error("Image compression failed, falling back to original:", error);
    return file;
  }
};

// ─── CUSTOMERS ───────────────────────────────────────────────────────────────

/**
 * Fetch paginated customers list with optional search query.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCustomers = (params) => api.get("/customers", { params });

/**
 * Fetch customers associated with an employer name.
 * @param {string} name - Employer name string.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCustomersByEmployer = (name) =>
  api.get("/customers/by-employer", { params: { name } });

/**
 * Fetch customer profile details by ID.
 * @param {string} id - Customer ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCustomer = (id) => api.get(`/customers/${id}`);

/**
 * Create a new customer profile.
 * @param {Object} data - Customer payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const createCustomer = (data) => api.post("/customers", data);

/**
 * Update customer profile by ID.
 * @param {string} id - Customer ID.
 * @param {Object} data - Customer update payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);

/**
 * Delete customer profile by ID.
 * @param {string} id - Customer ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);

/**
 * Fetch cheques history cashed by a customer.
 * @param {string} id - Customer ID.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCustomerCheques = (id, params) =>
  api.get(`/customers/${id}/cheques`, { params });

/**
 * Add identification document entry to customer.
 * @param {string} id - Customer ID.
 * @param {Object} data - Identification payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const addIdentification = (id, data) =>
  api.post(`/customers/${id}/identification`, data);

/**
 * Add contact entry to customer.
 * @param {string} id - Customer ID.
 * @param {Object} data - Contact payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const addContact = (id, data) =>
  api.post(`/customers/${id}/contact`, data);

/**
 * Block customer with reason.
 * @param {string} id - Customer ID.
 * @param {string} reason - Block reason.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const blockCustomer = (id, reason) =>
  api.put(`/customers/${id}/block`, { reason });

/**
 * Unblock customer by ID.
 * @param {string} id - Customer ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const unblockCustomer = (id) => api.put(`/customers/${id}/unblock`);

/**
 * Fetch all blocked customers list.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getBlockedCustomers = () => api.get("/customers/blocked");

// ── AI ID Scan ────────────────────────────────────────────────────

/**
 * Perform AI Vision OCR scan on Driver's License or State ID image.
 * @param {string} image - Base64 encoded image string.
 * @param {string} mimeType - Image mimeType.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const scanDriversLicense = (image, mimeType) =>
  api.post("/scan/id", { image, mimeType }, { timeout: 30000 });

/**
 * Perform AI Vision OCR scan on cheque image.
 * @param {string} image - Base64 encoded image string.
 * @param {string} mimeType - Image mimeType.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const scanChequeImage = (image, mimeType) =>
  api.post("/scan/cheque", { image, mimeType }, { timeout: 30000 });

// ── AI Check Crop ─────────────────────────────────────────────────

/**
 * Auto-crop cheque bounding box area from full document photo.
 * @param {string} image - Base64 encoded image string.
 * @param {string} mimeType - Image mimeType.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const cropCheckImage = (image, mimeType) =>
  api.post("/scan/crop-cheque", { image, mimeType }, { timeout: 20000 });

export const cropDocumentImage = cropCheckImage;

/**
 * Detect cheque crop boundary coordinates.
 * @param {string} image - Base64 encoded image string.
 * @param {string} mimeType - Image mimeType.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const detectCheckCropBounds = (image, mimeType) =>
  api.post("/scan/crop-bounds", { image, mimeType }, { timeout: 20000 });

// ─── CHEQUES ─────────────────────────────────────────────────────────────────

/**
 * Fetch paginated cheques list matching query parameters.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCheques = (params) => api.get("/cheques", { params });

/**
 * Fetch cheque counts grouped by maker company.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getChequeCounts = () => api.get("/cheques/counts-by-company");

/**
 * Trigger backfill linking cheques to maker company master records.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const backfillMakerCompany = () =>
  api.post("/cheques/backfill-maker-company");

/**
 * Fetch cheque details by ID.
 * @param {string} id - Cheque ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCheque = (id) => api.get(`/cheques/${id}`);

/**
 * Process and cash a new cheque.
 * @param {Object} data - Cheque creation payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const cashCheque = (data) => api.post("/cheques", data);

/**
 * Modify existing cheque details.
 * @param {string} id - Cheque ID.
 * @param {Object} data - Cheque update payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const modifyCheque = (id, data) => api.put(`/cheques/${id}`, data);

/**
 * Delete/void cheque record by ID.
 * @param {string} id - Cheque ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const deleteCheque = (id) => api.delete(`/cheques/${id}`);

/**
 * Fetch processing statistics for dashboard summary.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getDashboardStats = () => api.get("/cheques/stats/dashboard");

/**
 * Recrop stored cheque image (front/back).
 * @param {string} chequeId - Cheque ID.
 * @param {string} side - 'front' or 'back'.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const recropStoredChequeImage = (chequeId, side) =>
  api.post(`/cheques/${chequeId}/recrop`, { side }, { timeout: 15000 });

// ─── AUTH ─────────────────────────────────────────────────────────────────────

/**
 * Authenticate user credentials.
 * @param {Object} data - Login credentials object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const loginUser = (data) => api.post("/auth/login", data);

/**
 * Register a new user account.
 * @param {Object} data - User registration payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const registerUser = (data) => api.post("/auth/register", data);

/**
 * Fetch currently logged-in user profile.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getMe = () => api.get("/auth/me");

/**
 * Change current user password.
 * @param {Object} data - Password payload object containing currentPassword and newPassword.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const changePassword = (data) => api.put("/auth/change-password", data);

/**
 * Fetch all registered users list.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getUsers = () => api.get("/auth/users");

// ─── IMAGE UPLOADS (multipart/form-data) ─────────────────────────────────────

/**
 * Upload customer profile photo.
 * @async
 * @param {string} customerId - Customer ID.
 * @param {File} file - Profile photo File.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const uploadCustomerProfile = async (customerId, file) => {
  const compressed = await compressImageIfNeeded(file);
  const fd = new FormData();
  fd.append("profile", compressed);
  return api.post(`/upload/customer/${customerId}/profile`, fd);
};

/**
 * Upload customer ID front photo.
 * @async
 * @param {string} customerId - Customer ID.
 * @param {File} file - ID front photo File.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const uploadCustomerIdFront = async (customerId, file) => {
  const compressed = await compressImageIfNeeded(file);
  const fd = new FormData();
  fd.append("idFront", compressed);
  return api.post(`/upload/customer/${customerId}/id-front`, fd);
};

/**
 * Upload customer ID back photo.
 * @async
 * @param {string} customerId - Customer ID.
 * @param {File} file - ID back photo File.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const uploadCustomerIdBack = async (customerId, file) => {
  const compressed = await compressImageIfNeeded(file);
  const fd = new FormData();
  fd.append("idBack", compressed);
  return api.post(`/upload/customer/${customerId}/id-back`, fd);
};

/**
 * Upload cheque front image.
 * @async
 * @param {string} chequeId - Cheque ID.
 * @param {File} file - Cheque front image File.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const uploadChequeFront = async (chequeId, file) => {
  const compressed = await compressImageIfNeeded(file);
  const fd = new FormData();
  fd.append("checkFront", compressed);
  return api.post(`/upload/cheque/${chequeId}/front`, fd);
};

/**
 * Upload cheque back image.
 * @async
 * @param {string} chequeId - Cheque ID.
 * @param {File} file - Cheque back image File.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const uploadChequeBack = async (chequeId, file) => {
  const compressed = await compressImageIfNeeded(file);
  const fd = new FormData();
  fd.append("checkBack", compressed);
  return api.post(`/upload/cheque/${chequeId}/back`, fd);
};

// ─── BLOCKED COMPANIES ────────────────────────────────────────────────────────

/**
 * Fetch blocked companies list.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getBlockedCompanies = (params) =>
  api.get("/blocked-companies", { params });

/**
 * Add company to blocked companies list.
 * @param {Object} data - Blocked company payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const addBlockedCompany = (data) => api.post("/blocked-companies", data);

/**
 * Update blocked company entry by ID.
 * @param {string} id - Blocked company ID.
 * @param {Object} data - Blocked company payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateBlockedCompany = (id, data) =>
  api.put(`/blocked-companies/${id}`, data);

/**
 * Remove company from blocked list by ID.
 * @param {string} id - Blocked company ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const removeBlockedCompany = (id) =>
  api.delete(`/blocked-companies/${id}`);

/**
 * Check if company name exists on blocked list.
 * @param {string} name - Company name string.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const checkBlockedCompany = (name) =>
  api.get("/blocked-companies/check", { params: { name } });

/**
 * Fetch employer names for search typeahead autocomplete.
 * @param {string} [q] - Search query string.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getEmployerNames = (q) =>
  api.get("/customers/employers", q ? { params: { q } } : {});

// ─── BANK MASTER ──────────────────────────────────────────────────────────────

/**
 * Fetch banks list.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getBanks = (params) => api.get("/banks", { params });

/**
 * Fetch bank details by ID.
 * @param {string} id - Bank ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getBank = (id) => api.get(`/banks/${id}`);

/**
 * Create new bank entry.
 * @param {Object} data - Bank payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const createBank = (data) => api.post("/banks", data);

/**
 * Update bank entry by ID.
 * @param {string} id - Bank ID.
 * @param {Object} d - Bank update payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateBank = (id, d) => api.put(`/banks/${id}`, d);

/**
 * Delete bank entry by ID.
 * @param {string} id - Bank ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const deleteBank = (id) => api.delete(`/banks/${id}`);

// ─── COMPANY MASTER ───────────────────────────────────────────────────────────

/**
 * Fetch companies master list.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCompanies = (params) => api.get("/companies", { params });

/**
 * Trigger company sync from cheque transaction history.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const syncCompaniesFromCheques = () =>
  api.post("/companies/sync-from-cheques");

/**
 * Trigger full company sync.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const fullSyncCompanies = () => api.post("/companies/full-sync");

/**
 * Fetch company details by ID.
 * @param {string} id - Company ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCompany = (id) => api.get(`/companies/${id}`);

/**
 * Fetch company by name string.
 * @param {string} name - Company name.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getCompanyByName = (name) =>
  api.get("/companies/by-name", { params: { name } });

/**
 * Create a new company master record.
 * @param {Object} data - Company payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const createCompany = (data) => api.post("/companies", data);

/**
 * Update company master record by ID.
 * @param {string} id - Company ID.
 * @param {Object} d - Company update payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateCompany = (id, d) => api.put(`/companies/${id}`, d);

/**
 * Delete company master record by ID.
 * @param {string} id - Company ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const deleteCompany = (id) => api.delete(`/companies/${id}`);

/**
 * Block company from processing cheques.
 * @param {string} id - Company ID.
 * @param {string} reason - Block reason.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const blockCompany = (id, reason) =>
  api.put(`/companies/${id}/block`, { reason });

/**
 * Unblock company by ID.
 * @param {string} id - Company ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const unblockCompany = (id) => api.put(`/companies/${id}/unblock`);

/**
 * Check if company is blocked.
 * @param {string} name - Company name.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const checkCompanyBlocked = (name) =>
  api.get("/companies/check", { params: { name } });

/**
 * Set reference cheque image for company master.
 * @param {string} id - Company ID.
 * @param {string} imageUrl - Reference image URL.
 * @param {string} chequeId - Cheque ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const setCompanyReferenceImage = (id, imageUrl, chequeId) =>
  api.put(`/companies/${id}/reference-image`, { imageUrl, chequeId });

// ─── SCANNER PROXY ────────────────────────────────────────────────────────────
const SCANNER_API_URLS = {
  live:  'https://api.securecheckapp.us/api',
  stage: 'https://apidemo.securecheckapp.us/api',
};
const _ENV          = process.env.REACT_APP_ENV || 'live';
const _IS_LOCAL     = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const SCANNER_BASE  = _IS_LOCAL
  ? 'http://localhost:5000/api'
  : (SCANNER_API_URLS[_ENV] || SCANNER_API_URLS.live);

const scannerApi = axios.create({
  baseURL: SCANNER_BASE,
  headers: { 'Content-Type': 'application/json' },
});

scannerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Check connectivity and status of Epson hardware scanner proxy.
 * @param {string} ip - Scanner IP address.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const scannerStatus = (ip) => scannerApi.post('/scanner/status', { ip });

/**
 * Initiate hardware document scan and download JPEG blob stream.
 * @param {string} ip - Scanner IP address.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const scannerScan   = (ip) => scannerApi.post('/scanner/scan', { ip }, { responseType: 'blob' });

// ─── SUPER ADMIN ──────────────────────────────────────────────────────────────

/**
 * Fetch tenants list.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getTenants = () => api.get("/super-admin/tenants");

/**
 * Create new tenant account.
 * @param {Object} data - Tenant payload object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const createTenant = (data) => api.post("/super-admin/tenants", data);

/**
 * Fetch tenant details by ID.
 * @param {string} id - Tenant ID.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getTenantDetails = (id) => api.get(`/super-admin/tenants/${id}`);

/**
 * Update tenant subscription status.
 * @param {string} id - Tenant ID.
 * @param {string} status - New status ('Active', 'Suspended', etc.).
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateTenantStatus = (id, status) => api.put(`/super-admin/tenants/${id}/status`, { status });

/**
 * Update tenant subscription plan.
 * @param {string} id - Tenant ID.
 * @param {Object} data - Plan details object.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const updateTenantPlan = (id, data) => api.put(`/super-admin/tenants/${id}/plan`, data);

/**
 * Fetch platform audit logs.
 * @param {Object} [params] - Query parameters.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const getAuditLogs = (params) => api.get("/super-admin/audit-logs", { params });

// ─── AUTH 2FA OTP ─────────────────────────────────────────────────────────────

/**
 * Verify OTP authentication code.
 * @param {string} email - User email address.
 * @param {string} otp - OTP code string.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const verifyOtp = (email, otp) => api.post("/auth/verify-otp", { email, otp });

/**
 * Resend OTP authentication code to user email.
 * @param {string} email - User email address.
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const resendOtp = (email) => api.post("/auth/resend-otp", { email });


